import type { StageColor, StageRemoved, StageRow } from "@/lib/types";

/**
 * A tenant's stages (ADR-077 in `platform`): the catalog an admin manages on the Stages page, and the
 * one every stage label and picker in the console reads.
 *
 * The labels are read everywhere, synchronously, by `stageText` — a badge in a table row, a line in
 * the timeline — so the catalog lives in a small registry the app shell fills once it has loaded
 * (`setStageCatalog`). Until then, and against a platform that has no catalog yet, the built-in
 * thirteen in `lib/contacts.ts` answer, exactly as before. This module imports no other lib module,
 * so `lib/contacts.ts` can read it without a cycle.
 */

export const STAGE_GROUPS = ["working", "scheduled", "exit"] as const;
export type StageGroupName = (typeof STAGE_GROUPS)[number];
export const EXIT_SCOPES = ["same_firm", "all_firms"] as const;

/** A stage's machine name: what an outcome, a line rule and a voicemail message store. */
export const STAGE_SLUG_RE = /^[a-z][a-z0-9_]{0,39}$/;
export const STAGE_LABEL_MAX = 80;
export const STAGE_DESCRIPTION_MAX = 300;

let registry: readonly StageRow[] | null = null;

/** The app shell's call, when the catalog loads or changes. Null or empty: the built-in stages. */
export function setStageCatalog(rows: readonly StageRow[] | null | undefined): void {
  registry = rows && rows.length > 0 ? [...rows] : null;
}

/** The loaded catalog, in its order, or null while the built-in stages answer. */
export function stageCatalog(): readonly StageRow[] | null {
  return registry;
}

export function catalogRow(slug: string | null | undefined): StageRow | undefined {
  return slug ? registry?.find((s) => s.slug === slug) : undefined;
}

const GROUP: Record<StageGroupName, { label: string; description: string }> = {
  working: { label: "Working", description: "Still being worked: the dialer and the reps keep at it." },
  scheduled: { label: "Scheduled", description: "Something is booked; the lead is not dialed before it." },
  exit: {
    label: "Exit",
    description: "Done. Only another exit or an admin's reopen moves it, and it leaves every dial list.",
  },
};

export function groupText(group: string): { label: string; description: string } {
  return GROUP[group as StageGroupName] ?? { label: group, description: "Not a group this console knows." };
}

// --- the headings stages are listed under ---------------------------------------------------------
//
// The platform has three groups, and MIA is an exit: a signed client the firm could not reach
// keeps what an exit protects (no working outcome moves them, no other campaign calls them). But
// an exit reads as "done", and MIA is the one end stage that is called again, by the campaign
// that gives it a cadence (ADR-083, ADR-087 in `platform`). Listed among the finished stages it
// had the owner asking why an exit was being dialed. So the console lists it under a heading of
// its own, Recovery. Only where stages are listed: the stage's group, and every rule that reads
// it, is the platform's and stays `exit`.

/** The one end stage that is worked again. */
export const RECOVERY_STAGE = "mia";
export const STAGE_SECTIONS = ["working", "scheduled", "recovery", "exit"] as const;
export type StageSection = (typeof STAGE_SECTIONS)[number];

/** The heading a stage is listed under: its group, but Recovery for MIA. */
export function stageSection(slug: string, group: StageGroupName): StageSection {
  return group === "exit" && slug === RECOVERY_STAGE ? "recovery" : group;
}

const RECOVERY = {
  label: "Recovery",
  description:
    "Signed, and the firm could not reach them. Protected like an exit, and called again where the stage has a cadence.",
};

export function sectionText(section: string): { label: string; description: string } {
  return section === "recovery" ? RECOVERY : groupText(section);
}

const SCOPE: Record<string, { label: string; description: string }> = {
  same_firm: {
    label: "This firm's records",
    description: "Other records with the same phone follow it only when they are the same firm's case.",
  },
  all_firms: {
    label: "Every record with the phone",
    description: "Every record with the same phone follows it, whatever the firm (as Signed and Do not call do).",
  },
};

export function exitScopeText(scope: string | null): { label: string; description: string } | null {
  return scope ? SCOPE[scope] ?? { label: scope, description: "" } : null;
}

// --- the Stages page's form ------------------------------------------------------------------------

export interface StageValues {
  slug: string;
  label: string;
  description: string;
  group: StageGroupName;
  exit_scope: "same_firm" | "all_firms";
  dial_boost: boolean;
  /** The board column's tint; absent or "" for the pick by group. */
  color?: StageColor | "";
}

export const NEW_STAGE: StageValues = {
  slug: "",
  label: "",
  description: "",
  group: "working",
  exit_scope: "same_firm",
  dial_boost: false,
};

export function stageValues(row: StageRow): StageValues {
  return {
    slug: row.slug,
    label: row.label,
    description: row.description ?? "",
    group: row.group,
    exit_scope: row.exit_scope ?? "same_firm",
    dial_boost: row.dial_boost,
    ...(row.color ? { color: row.color } : {}),
  };
}

/** A slug suggested from a label: "Retainer sent" -> "retainer_sent". */
export function slugFromLabel(label: string): string {
  const slug = label
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .replace(/^[0-9_]+/, "");
  return slug.slice(0, 40);
}

/** Why the form cannot be saved, by field. */
export function stageProblems(values: StageValues, taken: readonly string[], creating: boolean): Partial<Record<keyof StageValues, string>> {
  const out: Partial<Record<keyof StageValues, string>> = {};
  if (!values.label.trim()) out.label = "Give the stage a name.";
  else if (values.label.trim().length > STAGE_LABEL_MAX) out.label = `At most ${STAGE_LABEL_MAX} characters.`;
  if (values.description.length > STAGE_DESCRIPTION_MAX) {
    out.description = `At most ${STAGE_DESCRIPTION_MAX} characters.`;
  }
  if (creating) {
    if (!STAGE_SLUG_RE.test(values.slug)) {
      out.slug = "Lowercase letters, digits and _, starting with a letter; at most 40.";
    } else if (taken.includes(values.slug)) {
      out.slug = "Another stage already has this name.";
    }
  }
  return out;
}

/** What a create sends. An exit carries its reach and never a boost; any other stage the reverse. */
export function newStagePayload(values: StageValues) {
  const exit = values.group === "exit";
  return {
    slug: values.slug,
    label: values.label.trim(),
    description: values.description.trim() || null,
    group: values.group,
    exit_scope: exit ? values.exit_scope : null,
    dial_boost: exit ? false : values.dial_boost,
    ...(values.color ? { color: values.color } : {}),
  };
}

/** What an edit sends: only what changed. A system stage sends its words alone. */
export function stageChanges(values: StageValues, row: StageRow) {
  const out: Record<string, unknown> = {};
  if (values.label.trim() !== row.label) out.label = values.label.trim();
  if ((values.description.trim() || null) !== (row.description ?? null)) {
    out.description = values.description.trim() || null;
  }
  // A tint is the board's look, so a system stage takes one too.
  if ((values.color || null) !== (row.color ?? null)) out.color = values.color || null;
  if (row.system) return out;
  if (values.group !== row.group) out.group = values.group;
  const exit = values.group === "exit";
  const scope = exit ? values.exit_scope : null;
  if (scope !== (row.exit_scope ?? null) || (exit && values.group !== row.group)) out.exit_scope = scope;
  const boost = exit ? false : values.dial_boost;
  if (boost !== row.dial_boost) out.dial_boost = boost;
  return out;
}

/** Why a stage cannot move between the exits and the rest now, or null. */
export function crossingProblem(row: StageRow, group: StageGroupName): string | null {
  if ((group === "exit") === (row.group === "exit") || row.leads === 0) return null;
  return `${row.leads} lead${row.leads === 1 ? " is" : "s are"} in it: move ${row.leads === 1 ? "it" : "them"} to another stage first.`;
}

// --- deleting and ordering ------------------------------------------------------------------------------

/** The stages a deleted one's leads can move to: the others on its side of the exits. */
export function replacementsFor(rows: readonly StageRow[], slug: string): StageRow[] {
  const gone = rows.find((r) => r.slug === slug);
  if (!gone) return [];
  const exit = gone.group === "exit";
  return rows.filter((r) => r.slug !== slug && (r.group === "exit") === exit);
}

/** The whole order after moving `slug` one place up or down within its group, or null when it is
 *  already at that end. The API takes every stage, once. */
export function movedWithinGroup(rows: readonly StageRow[], slug: string, by: -1 | 1): string[] | null {
  const order = rows.map((r) => r.slug);
  const at = order.indexOf(slug);
  const other = at + by;
  if (at < 0 || other < 0 || other >= rows.length || rows[other]!.group !== rows[at]!.group) return null;
  const next = [...order];
  [next[at], next[other]] = [next[other]!, next[at]!];
  return next;
}

/** What deleting a stage did, in a sentence. An exit's stage cadences are removed, not moved
 *  (platform ADR-077): moved, the campaign would call every lead in the replacement exit. */
export function removedSummary(result: StageRemoved, replacementLabel: string | null, exit = false): string {
  if (!result.replaced_by) return "Nothing used it.";
  const cadences = result.stage_cadences
    ? `${result.stage_cadences} stage cadence${result.stage_cadences === 1 ? "" : "s"}`
    : null;
  const parts = [
    result.leads && `${result.leads} lead${result.leads === 1 ? "" : "s"}`,
    result.dispositions && `${result.dispositions} outcome${result.dispositions === 1 ? "" : "s"}`,
    result.line_rules && `${result.line_rules} line rule${result.line_rules === 1 ? "" : "s"}`,
    result.voicemail_messages &&
      `${result.voicemail_messages} voicemail message${result.voicemail_messages === 1 ? "" : "s"}`,
    !exit && cadences,
  ].filter(Boolean);
  const said = [
    parts.length ? `${parts.join(", ")} moved to ${replacementLabel ?? result.replaced_by}.` : null,
    exit && cadences ? `${cadences} removed.` : null,
  ].filter(Boolean);
  return said.length ? said.join(" ") : "Nothing used it.";
}

/**
 * A column's tint as classes: the stage's own colour, else one by its group (the open stages blue,
 * the scheduled ones a deeper blue, the exits peach, Do not call red). The named colours map onto
 * the design system's ramps, so both themes keep their contrast.
 */
export const STAGE_TINT: Record<StageColor, string> = {
  blue: "bg-accent-3",
  indigo: "bg-accent-4",
  purple: "bg-tint-purple",
  pink: "bg-tint-pink",
  red: "bg-danger-4",
  orange: "bg-tint-orange",
  peach: "bg-warning-4",
  yellow: "bg-tint-yellow",
  green: "bg-success-4",
  teal: "bg-tint-teal",
  gray: "bg-gray-4",
};

export function stageTint(slug: string, color: StageColor | null | undefined, group: StageGroupName | null): string {
  if (color) return STAGE_TINT[color];
  if (slug === "dnc") return STAGE_TINT.red;
  if (group === "scheduled") return STAGE_TINT.indigo;
  if (group === "exit") return STAGE_TINT.peach;
  return STAGE_TINT.blue;
}
